// 🔒 Permission utility functions for CRUD operations

/**
 * Check if user has permission for a specific action
 * @param {Array} rolePermissionList - List of role permissions from context
 * @param {string} moduleName - Name of the module (e.g., "Leads", "Quotation")
 * @param {string} permission - Type of permission (list_permission, create_permission, view_permission, edit_permission, delete_permission)
 * @returns {boolean} - True if user has permission
 */
export const checkPermission = (rolePermissionList, moduleName, permission) => {
  if (!rolePermissionList || !Array.isArray(rolePermissionList)) {
    console.warn("[SECURITY] Invalid rolePermissionList provided");
    return false;
  }

  const target = (moduleName || "").toLowerCase().trim();
  const hasPermission = rolePermissionList.some((element) => {
    const elemName = (element["name"] || "").toLowerCase().trim();
    const isMatch =
      elemName === target ||
      elemName === `${target}s` ||
      `${elemName}s` === target;
    return isMatch && element["menu_permission"] === true && element[permission] === true;
  });

  console.log(`[SECURITY] Permission check - Module: ${moduleName}, Permission: ${permission}, Allowed: ${hasPermission}`);
  return hasPermission;
};

/**
 * Check if user can create a new record
 * @param {Array} rolePermissionList - List of role permissions
 * @param {string} moduleName - Name of the module
 * @returns {boolean}
 */
export const canCreate = (rolePermissionList, moduleName) => {
  return checkPermission(rolePermissionList, moduleName, "create_permission");
};

/**
 * Check if user can view/access a record
 * @param {Array} rolePermissionList - List of role permissions
 * @param {string} moduleName - Name of the module
 * @returns {boolean}
 */
export const canView = (rolePermissionList, moduleName) => {
  return checkPermission(rolePermissionList, moduleName, "view_permission");
};

/**
 * Check if user can edit a record
 * @param {Array} rolePermissionList - List of role permissions
 * @param {string} moduleName - Name of the module
 * @returns {boolean}
 */
export const canEdit = (rolePermissionList, moduleName) => {
  return checkPermission(rolePermissionList, moduleName, "edit_permission");
};

/**
 * Check if user can delete a record
 * @param {Array} rolePermissionList - List of role permissions
 * @param {string} moduleName - Name of the module
 * @returns {boolean}
 */
export const canDelete = (rolePermissionList, moduleName) => {
  return checkPermission(rolePermissionList, moduleName, "delete_permission");
};

/**
 * Check if user can list records
 * @param {Array} rolePermissionList - List of role permissions
 * @param {string} moduleName - Name of the module
 * @returns {boolean}
 */
export const canList = (rolePermissionList, moduleName) => {
  return checkPermission(rolePermissionList, moduleName, "list_permission");
};

/**
 * Check if the role can see sensitive/restricted fields for a module
 * (e.g. cost pricing on Item). Backed by the existing additional_permission
 * flag on the menu's role_permissions row.
 * @param {Array} rolePermissionList - List of role permissions
 * @param {string} moduleName - Name of the module
 * @returns {boolean}
 */
export const canViewSensitiveFields = (rolePermissionList, moduleName) => {
  return checkPermission(rolePermissionList, moduleName, "additional_permission");
};

/**
 * Get allowed CRUD operations for a module
 * @param {Array} rolePermissionList - List of role permissions
 * @param {string} moduleName - Name of the module
 * @returns {Object} - Object with allowed operations
 */
export const getAllowedOperations = (rolePermissionList, moduleName) => {
  return {
    canList: canList(rolePermissionList, moduleName),
    canCreate: canCreate(rolePermissionList, moduleName),
    canView: canView(rolePermissionList, moduleName),
    canEdit: canEdit(rolePermissionList, moduleName),
    canDelete: canDelete(rolePermissionList, moduleName),
  };
};

/**
 * Show permission denied message
 * @param {string} action - Action attempted (e.g., "delete", "edit")
 * @param {Function} setSnackbarMessage - State setter for snackbar message
 */
export const showPermissionDenied = (action, setSnackbarMessage) => {
  const message = `You don't have permission to ${action} this record.`;
  console.warn(`[SECURITY] ${message}`);
  if (setSnackbarMessage) {
    setSnackbarMessage(message);
  }
};
